/**
 * Exhaustive switch/case guard.
 *
 * @module assertNever
 */

/**
 * Throws at runtime when a code path that must be unreachable is reached.
 *
 * Place this in the `default` branch of a `switch` or at the end of an
 * `if`/`else if` chain over a discriminated union. TypeScript will report a
 * compile-time error if any variant is unhandled, and the throw protects
 * against unexpected values at runtime.
 *
 * @param value - The value that was not handled. TypeScript narrows this to
 *   `never` when every variant is covered.
 * @returns Never returns; always throws.
 *
 * @example
 * ```ts
 * type Status = "active" | "inactive";
 *
 * function label(status: Status): string {
 *   switch (status) {
 *     case "active":
 *       return "Active";
 *     case "inactive":
 *       return "Inactive";
 *     default:
 *       return assertNever(status);
 *   }
 * }
 * ```
 */
export function assertNever(value: never): never {
  throw new Error(`Unexpected value: ${String(value)}`);
}
